// The Scout tab's Approve card (GRO-162 §2.2): the data the dashboard sends, and the few rules
// the panel applies to it. The words are the dashboard's (`board/approval_card.py`); the page
// renders them as plain text and decides nothing about what a click may do.
//
// One card holds every pending approval, and one click answers them all: eve resumes a step only
// when one delivery answers every approval in it, so a card per call could park a write.

// `add_to_sequence`'s dropdown (P4-02): the person's own sequences, newest first, and Scout's pick
// when it is one of them. Null on every other item.
export interface SequenceOption {
  id: string;
  name: string;
  status: string;
}

export interface SequencePicker {
  options: SequenceOption[];
  selected_id: string | null;
  truncated: boolean;
}

export interface ApprovalItem {
  request_id: string;
  tool_name: string;
  title: string;
  lines: string[];
  note: string | null;
  sequence_picker: SequencePicker | null;
}

// The sequence picked on each dropdown, by request id: what the click sends.
export type SequenceChoices = Record<string, string>;

export interface ApprovalCardData {
  request_ids: string[];
  items: ApprovalItem[];
  // False when the card cannot show exactly what Approve would write (the queue was unreadable):
  // then it offers only Cancel, and the server refuses an Approve anyway.
  approvable: boolean;
}

export type ApprovalDecision = 'approve' | 'cancel';

// A resumed turn that outlasts one read is re-read this often, for at most this many tries
// (three minutes: a 25-row list is a door read and a write per row).
export const WORKING_POLL_MS = 5000;
export const WORKING_POLL_LIMIT = 36;

// Shown, and on the composer, while a resumed turn runs: nothing new is sent until it ends.
export const WORKING_NOTICE = 'Scout is still finishing the last action…';

function stringsOf(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

/** The dropdown from an item, or null when it is absent or has nothing to pick. */
export function parseSequencePicker(raw: unknown): SequencePicker | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as { options?: unknown; selected_id?: unknown; truncated?: unknown };
  const options: SequenceOption[] = [];
  for (const entry of Array.isArray(record.options) ? record.options : []) {
    if (!entry || typeof entry !== 'object') continue;
    const option = entry as Record<string, unknown>;
    if (typeof option.id !== 'string' || !option.id || typeof option.name !== 'string') continue;
    options.push({ id: option.id, name: option.name, status: typeof option.status === 'string' ? option.status : '' });
  }
  if (options.length === 0) return null;
  const selected = typeof record.selected_id === 'string' && options.some(option => option.id === record.selected_id)
    ? record.selected_id : null;
  return { options, selected_id: selected, truncated: record.truncated === true };
}

/** The picks a card starts with: each dropdown's preselection. */
export function initialSequenceChoices(card: ApprovalCardData): SequenceChoices {
  const choices: SequenceChoices = {};
  for (const item of card.items) {
    if (item.sequence_picker?.selected_id) choices[item.request_id] = item.sequence_picker.selected_id;
  }
  return choices;
}

/** Whether every dropdown on the card has a pick, so Approve may go. */
export function everySequencePicked(card: ApprovalCardData, choices: SequenceChoices): boolean {
  return card.items.every(item => !item.sequence_picker || Boolean(choices[item.request_id]));
}

/** The card from a reply, or null when it is absent or malformed. Junk never renders a button. */
export function parseApprovalCard(raw: unknown): ApprovalCardData | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as { request_ids?: unknown; items?: unknown; approvable?: unknown };
  const items: ApprovalItem[] = [];
  for (const entry of Array.isArray(record.items) ? record.items : []) {
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;
    if (typeof item.request_id !== 'string' || !item.request_id || typeof item.title !== 'string') continue;
    items.push({
      request_id: item.request_id,
      tool_name: typeof item.tool_name === 'string' ? item.tool_name : '',
      title: item.title,
      lines: stringsOf(item.lines),
      note: typeof item.note === 'string' && item.note ? item.note : null,
      sequence_picker: parseSequencePicker(item.sequence_picker),
    });
  }
  const requestIds = stringsOf(record.request_ids);
  if (items.length === 0 || requestIds.length === 0) return null;
  // Only an explicit true offers Approve: a card that says nothing is not a card that may write.
  return { request_ids: requestIds, items, approvable: record.approvable === true };
}

/** The card after a reply: replaced only when the reply says the pending set changed. */
export function nextApprovalCard(
  current: ApprovalCardData | null,
  reply: { approval_changed?: boolean; approval?: unknown },
): ApprovalCardData | null {
  if (!reply.approval_changed) return current;
  return parseApprovalCard(reply.approval);
}

export function approveLabel(card: ApprovalCardData): string {
  return card.items.length > 1 ? `Approve all ${card.items.length}` : 'Approve';
}

/** Why the buttons are off, or null when a click may go. The server refuses these anyway.
 * An impersonating admin (W13) clicks as the target, so their view-as does not block. */
export function approvalBlockedReason(viewingAs: string | null, busy: boolean, impersonating = false): string | null {
  if (viewingAs && !impersonating) return 'You are viewing as someone else, so approvals are off.';
  if (busy) return 'Working…';
  return null;
}

/** The line the transcript keeps for a click, so the history says what was decided. */
export function decisionLine(card: ApprovalCardData, decision: ApprovalDecision): string {
  const titles = card.items.map(item => item.title).join('; ');
  return decision === 'approve' ? `Approved: ${titles}` : `Cancelled: ${titles}`;
}

/** Scout says nothing after a Cancel, by design; the panel says it plainly instead. */
export function replyAfterDecision(decision: ApprovalDecision, reply: string | null | undefined): string | null {
  const text = (reply || '').trim();
  if (text) return text;
  return decision === 'cancel' ? 'Cancelled. Nothing was done.' : null;
}

/** Where a still-running resumed turn is kept, per conversation, so a reload keeps re-reading it. */
export function workingStorageKey(conversationKey: string): string {
  return `scout-chat-working:${conversationKey}`;
}

export function parseWorkingIndex(raw: string | null): number | null {
  if (raw === null || !/^\d+$/.test(raw)) return null;
  return Number(raw);
}

/** Why a reply drew no card when it should have (a pending read cut short), or null. */
export function approvalNoteFrom(reply: { approval_changed?: boolean; approval?: unknown; approval_note?: unknown }): string | null {
  if (!reply.approval_changed || parseApprovalCard(reply.approval) !== null) return null;
  return typeof reply.approval_note === 'string' && reply.approval_note ? reply.approval_note : null;
}

// --- following a resumed turn (review round 3, fixes 4 and 5) --------------------------------

export const GAVE_UP_NOTICE = 'Scout is still working on this. Reload later to see the result.';
export const POLL_ERROR_NOTICE = "Couldn't reach the dashboard to read the result. Reload to try again.";
export const UNREACHABLE_NOTICE = "Scout couldn't be reached. Reload to try again.";

export interface WorkingReadStep {
  // Stop re-reading now.
  stop: boolean;
  // Forget the stored start index. Only a read that saw the turn end may: otherwise a reload
  // must resume the re-read, which is what makes "reload later" true.
  clearMarker: boolean;
  // Apply the reply to the transcript and the card.
  apply: boolean;
  message: string | null;
}

/** What one re-read of a resumed turn means for the polling loop. */
export function stepAfterRead(reply: { working?: boolean; unreachable?: boolean; available?: boolean; reason?: string | null }): WorkingReadStep {
  if (reply.working) return { stop: false, clearMarker: false, apply: false, message: null };
  if (reply.unreachable) return { stop: true, clearMarker: false, apply: false, message: reply.reason || UNREACHABLE_NOTICE };
  if (reply.available) return { stop: true, clearMarker: true, apply: true, message: null };
  return { stop: true, clearMarker: true, apply: false, message: reply.reason || 'Scout could not finish this.' };
}

// --- after a click on the card (GRO-255, #379's last Bugbot finding) --------------------------

// What one answer to `POST /api/chat/respond` means for the tab: redraw a stale card, show a
// refusal, follow the resumed turn from `startIndex`, or apply a reply that is already whole.
export type RespondStep =
  | { kind: 'stale' }
  | { kind: 'refused' }
  | { kind: 'follow'; startIndex: number }
  | { kind: 'apply' };

/**
 * A click Scout accepted carries where the resumed turn starts. A turn still running and a stream
 * read that could not reach Scout are both followed from there, so the marker is written and a
 * reload recovers the result; `unreachable` is not a refusal, the answer went through.
 */
export function stepAfterRespond(reply: {
  stale?: boolean; available?: boolean; working?: boolean; unreachable?: boolean; stream_start_index?: number | null; reason?: string | null;
}): RespondStep {
  if (reply.stale) return { kind: 'stale' };
  const startIndex = reply.stream_start_index;
  if ((reply.working || reply.unreachable) && typeof startIndex === 'number') return { kind: 'follow', startIndex };
  if (!reply.available) return { kind: 'refused' };
  return { kind: 'apply' };
}
